import { Core } from "@faire/design-tokens";
import { Avatar } from "@faire/web--source/slate/Avatar";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { trackRetailerTeamManagementMultiUserEvergreenHomepageBannerAvatarsClick } from "@faire/web-api--source/events/retailerTeamManagement/click/multiUserEvergreenHomepageBannerAvatars";
import { trackRetailerTeamManagementMultiUserEvergreenHomepageBannerView } from "@faire/web-api--source/events/retailerTeamManagement/view/multiUserEvergreenHomepageBanner";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ISimpleImageTitle } from "@faire/web-api--source/indigofair/data/ISimpleImageTitle";
import React from "react";
import styled from "styled-components";

import { TeamMembersModal } from "@faire/retailer/components/Modals/TeamMembersModal/TeamMembersModal";
import { useModalState } from "@faire/retailer/hooks/useModalState";

interface IProps {
  simpleImageTitle: ISimpleImageTitle;
  additionalUsers: string | undefined;
}

export const Avatars: React.FC<IProps> = ({
  simpleImageTitle,
  additionalUsers,
}) => {
  const { isOpen, openModal, closeModal } = useModalState();
  const actionRef = useDoActionWhenVisible(() => {
    trackRetailerTeamManagementMultiUserEvergreenHomepageBannerView();
  });

  const handleAvatarClick = () => {
    trackRetailerTeamManagementMultiUserEvergreenHomepageBannerAvatarsClick();
    openModal();
  };

  return (
    <Column ref={actionRef} justify="center">
      {isOpen ? <TeamMembersModal onClose={closeModal} /> : null}
      <AvatarRow align="center" onClick={handleAvatarClick}>
        {(simpleImageTitle.images ?? []).map((image: IImage, index) => {
          return (
            <AvatarWrapper
              data-test-id={`homepage-team-member-avatar-${index}`}
              key={index}
              $marginLeft={index === 0 ? 0 : -8}
            >
              <TeamMemberAvatar
                size="x-small"
                image={IImage.build({
                  url: image.url ?? "",
                })}
              />
            </AvatarWrapper>
          );
        })}
        {additionalUsers ? (
          <AdditionalUsersAvatar>
            <Typography variant="labelSansMedium" color={Core.text.inverse}>
              {additionalUsers}
            </Typography>
          </AdditionalUsersAvatar>
        ) : null}
      </AvatarRow>
    </Column>
  );
};

const AdditionalUsersAvatar = styled.div`
  width: ${getSpacing("4x")};
  height: ${getSpacing("4x")};
  background-color: ${Core.surface.default};
  border: 1px solid ${Core.surface.subdued};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: ${getSpacing("-1x")};
  z-index: 2;
`;

const AvatarRow = styled(Row)`
  width: fit-content;
  &:hover {
    cursor: pointer;
  }
`;

const AvatarWrapper = styled.div<{ $marginLeft: number }>`
  margin-left: ${({ $marginLeft }) => $marginLeft}px;
`;

const TeamMemberAvatar = styled(Avatar)`
  border: 1px solid ${Core.surface.subdued};
`;
